// Display catalogue for the public pricing surfaces: the homepage pricing
// section, the crawlable /pricing page and the prerendered guide CTAs.
//
// The authoritative prices live server-side in functions/lib/pricing.js and
// every total is recomputed there, so nothing here is ever trusted for
// payment. These two files must be kept in step: the pence values below are
// the same numbers, written for display.

export const packages = [
  {
    key: "risk",
    name: "Vehicle Risk Check",
    plate: "9.99",
    noFindNoFee: false,
    pricePence: 999,
    stamp: "RISK CHECK",
    copy: "Know the risks before you shake on it. The checks that stop a bad car being your problem.",
    items: ["Stolen vehicle marker", "Insurance write-off category", "Outstanding finance agreement", "Vehicle valuation where available", "Export and scrap history", "Recorded mileage verification", "VIC inspection record"],
    button: "Run the risk check",
  },
  {
    key: "complete",
    name: "Complete History Report",
    plate: "16.99",
    noFindNoFee: true,
    pricePence: 1699,
    stamp: "FULL HISTORY",
    copy: "The whole story, bonnet to boot. Everything above plus the service record the manufacturer holds for the car.",
    infoCopy: "The whole story, bonnet to boot. Everything in the Risk Check plus the service record the manufacturer holds for the car.",
    items: ["Everything in the risk check", "Manufacturer service history", "Dealer and garage trail", "Itemised service actions", "Vehicle valuation where available"],
    button: "Order the full report",
    featured: true,
  },
];

// How each product is charged. The two differ, and saying so plainly is the
// point: the promise only means something if it is clear what it covers.
// Mirrors functions/lib/order-products.js#retainedPenceOnNoRecords, which is
// what actually decides how much Stripe captures.
export const chargeNoteByKey = {
  risk: "Charged when you order. These checks run against data we can always reach, so there is no no-find-no-fee condition on this one.",
  complete: "Authorised when you order, charged only once manufacturer service records have been found. If there are none, the hold is released and you pay nothing.",
};

// ServiceLad sells the same two packages under its own names and lower prices.
// Same keys and contents; functions/lib/pricing.js holds the matching pence.
const serviceLadOverrides = {
  risk: {
    name: "Full HPI Check",
    plate: "7.95",
    pricePence: 795,
    stamp: "FULL HPI CHECK",
    copy: "The full HPI check for less: stolen, write-off, outstanding finance, mileage and more, for one car, one payment.",
    button: "Run the HPI check",
  },
  complete: {
    name: "Full HPI Check with Service History",
    plate: "14.95",
    pricePence: 1495,
    stamp: "HPI + SERVICE HISTORY",
    copy: "Everything in the full HPI check plus the service record the manufacturer holds for the car.",
    infoCopy: "Everything in the Full HPI Check plus the service record the manufacturer holds for the car.",
    items: ["Everything in the full HPI check", "Manufacturer service history", "Dealer and garage trail", "Itemised service actions", "Vehicle valuation where available"],
    button: "Order HPI + service history",
  },
};

// The packages as a brand sells them. Use this rather than `packages` on any
// page a ServiceLad visitor can see.
export function packagesFor(brandKey) {
  if (brandKey !== "servicelad") return packages;
  return packages.map((plan) => ({ ...plan, ...serviceLadOverrides[plan.key] }));
}

// Adding V5C alerts changes the deal on the Complete report, so it is stated
// wherever the promise is. The customer has shown they want the ongoing checks
// and the risk data as well, so we keep that part and release only the service
// history. Mirrors retainedPenceOnNoRecords in functions/lib/order-products.js.
export const v5cBundleNote = "If you add V5C alerts to the Complete History Report and no service records are found, we do not release the whole payment. We keep the £9.99 risk check plus the price of your V5C alerts, and release the rest, because the risk check and the monitoring are delivered either way.";

export function v5cBundleNoteFor(brandKey) {
  if (brandKey !== "servicelad") return v5cBundleNote;
  const [risk, complete] = packagesFor(brandKey);
  return `If you add V5C alerts to the ${complete.name} and no service records are found, we do not release the whole payment. We keep the £${risk.plate} ${risk.name} plus the price of your V5C alerts, and release the rest, because the HPI check and the monitoring are delivered either way.`;
}

// The one objection that stops people buying a service history report is
// "what if you find nothing?". Every brand answers it in its own voice. This
// applies to the Complete History Report only.
export const completeAssuranceByBrand = {
  serviceboss: "No manufacturer records found? Your payment will not be taken.",
  servicebyreg: "We only take payment when manufacturer service records are found.",
  servicelad: "If the manufacturer has no service records for the car, you will not be charged.",
};

// Optional extras, priced at checkout rather than as packages.
export const addOns = [
  { key: "v5c_alerts", name: "V5C logbook monitoring", plate: "1.49", copy: "Three months of alerts when DVLA reissues the logbook. Six and twelve month terms also available." },
];
